/**
 * Chart-bound conversation helpers. Conversations persist metadata.chartId;
 * history is scoped per chart in ThreadHistory. Opening a chart restores the last
 * thread the user used there (or the newest chart thread if none is remembered).
 */

export function conversationsForChart(threads, chartId, scope = 'chart') {
    const list = Array.isArray(threads) ? threads : [];
    if (scope !== 'chart' || !chartId) {
        return list;
    }
    return list.filter((thread) => thread?.metadata?.chartId === chartId);
}

export function latestConversationForChart(threads, chartId) {
    const scoped = conversationsForChart(threads, chartId, 'chart');
    if (scoped.length === 0) {
        return null;
    }
    return [...scoped].sort((a, b) => {
        const tb = Date.parse(b?.updatedAt || '') || 0;
        const ta = Date.parse(a?.updatedAt || '') || 0;
        return tb - ta;
    })[0];
}

export function conversationBelongsToChart(thread, chartId) {
    return Boolean(chartId && thread?.metadata?.chartId === chartId);
}

/** True only when the thread is known to belong to a different chart (not missing / pending metadata). */
export function conversationBelongsToDifferentChart(thread, chartId) {
    const threadChartId = thread?.metadata?.chartId;
    return Boolean(chartId && threadChartId && threadChartId !== chartId);
}

/**
 * Which conversation to show for a chart: the current thread if it belongs here
 * (or is not listed yet), else the last one the user used on this chart, else
 * the newest chart thread. `null` means start a new conversation.
 */
export function resolveConversationForChart({
    threads,
    chartId,
    currentThreadId = null,
    lastInteractedThreadId = null,
} = {}) {
    if (!chartId) {
        return currentThreadId || null;
    }

    const list = Array.isArray(threads) ? threads : [];
    const current = list.find((thread) => thread.id === currentThreadId);

    if (conversationBelongsToChart(current, chartId)) {
        return currentThreadId;
    }

    if (currentThreadId && !current) {
        if (!lastInteractedThreadId || lastInteractedThreadId === currentThreadId) {
            return currentThreadId;
        }
    }

    if (lastInteractedThreadId) {
        const remembered = list.find((thread) => thread.id === lastInteractedThreadId);
        if (conversationBelongsToChart(remembered, chartId)) {
            return lastInteractedThreadId;
        }
    }

    return latestConversationForChart(list, chartId)?.id || null;
}
